/**
 * Generic fetch wrapper for the FastAPI backend.
 *
 * Phase: 3 (introduced), 4+ (auth header injection)
 *
 * - Prepends "/api" to every path; callers say "/lookup", not "/api/lookup".
 * - JSON content-type by default; FormData bodies pass through unchanged.
 * - Throws ApiError on non-2xx with parsed body and status.
 * - Auto-injects Authorization: Bearer <token> when the path starts with
 *   "/admin/" (or when auth: true is passed explicitly). 401 responses clear
 *   the stored token so the ProtectedRoute kicks the user back to login.
 */

import { clearToken, getToken } from "../lib/auth";

const API_BASE = "/api";

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: unknown,
    message?: string,
  ) {
    super(message ?? `API error ${status}`);
    this.name = "ApiError";
  }
}

interface ApiFetchOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  /** Override auto-detection: force-attach (true) or omit (false) the bearer header. */
  auth?: boolean;
}

export async function apiFetch<T>(path: string, options: ApiFetchOptions = {}): Promise<T> {
  const { auth, body, headers, ...rest } = options;

  const finalHeaders: Record<string, string> = {
    ...((headers as Record<string, string> | undefined) ?? {}),
  };

  let finalBody: BodyInit | undefined;
  if (body instanceof FormData) {
    // Let the browser set the multipart boundary.
    finalBody = body;
  } else if (body !== undefined) {
    finalHeaders["Content-Type"] = "application/json";
    finalBody = JSON.stringify(body);
  }

  const requiresAuth = auth ?? path.startsWith("/admin");
  if (requiresAuth) {
    const token = getToken();
    if (token) finalHeaders.Authorization = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE}${path}`, {
    ...rest,
    headers: finalHeaders,
    body: finalBody,
  });

  if (res.status === 401 && finalHeaders.Authorization) {
    clearToken();
  }

  if (!res.ok) {
    let errorBody: unknown = null;
    try {
      errorBody = await res.json();
    } catch {
      // non-JSON response is fine
    }
    throw new ApiError(res.status, errorBody);
  }

  if (res.status === 204) return undefined as T;

  try {
    return (await res.json()) as T;
  } catch {
    return undefined as T;
  }
}
